import React, { useEffect, useState, useRef } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import SearchIcon from '@material-ui/icons/Search';
import axios from 'axios';
import { useSelector } from 'react-redux';
import envConfig from '../../../bin/env.config';
import { Typography, Box, CircularProgress, InputAdornment, TablePagination } from '@material-ui/core';

export default function ProductNoteReportDialog({ open, onClose, projectId }) {
  const token = useSelector((state) => state.session.token);
  const user = useSelector((state) => state.session.user);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [searchQuery, setSearchQuery] = useState('');
  const [total, setTotal] = useState(0);
  const [editingId, setEditingId] = useState(null);
  const [editingText, setEditingText] = useState('');
  const prevOpen = useRef(false);

  // Reset page and search when dialog opens
  useEffect(() => {
    if (open && projectId && !prevOpen.current) {
      setPage(0);
      setSearchQuery('');
    }
    prevOpen.current = open;
  }, [open, projectId]);

  // Reset page when search query changes
  useEffect(() => {
    if (open && projectId && prevOpen.current) {
      setPage(0);
    }
  }, [searchQuery]);

  // Fetch notes when open, page, rowsPerPage or searchQuery changes
  useEffect(() => {
    if (open && projectId) {
      const currentSkip = page * rowsPerPage;
      fetchNotes(currentSkip, rowsPerPage, searchQuery);
    }
  }, [open, projectId, page, rowsPerPage, searchQuery]);

  const fetchNotes = async (currentSkip, currentLimit, query) => {
    setLoading(true);
    try {
      const userID = user?._id || '';
      const url = `${envConfig.API_ROOT}${envConfig.API_VERSION}/company-report-tool/product-note-report?skip=${currentSkip}&limit=${currentLimit}&q=${encodeURIComponent(query)}`;

      const res = await axios.post(url, {
        company_report_tool_project_id: projectId,
        userID: userID,
      }, {
        headers: { Authorization: token },
      });

      setNotes(res.data?.data || []);
      setTotal(res.data?.total || 0);
    } catch (err) {
      setNotes([]);
      console.error('Error fetching notes:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value) => {
    setSearchQuery(value);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleEditNote = (note) => {
    setEditingId(note.company_report_tool_product_id);
    setEditingText(note.note);
  };

  const handleUpdateNote = async (id) => {
    if (!editingText.trim()) return;
    setLoading(true);
    try {
      await axios.post(`${envConfig.API_ROOT}${envConfig.API_VERSION}/company-report-tool/project-note-update`, {
        id,
        note: editingText,
      }, {
        headers: { Authorization: token },
      });
      setEditingId(null);
      setEditingText('');
      fetchNotes(page * rowsPerPage, rowsPerPage, searchQuery);
    } catch (err) {
      console.error('Error updating note:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteNote = async (id) => {
    setLoading(true);
    try {
      await axios.post(`${envConfig.API_ROOT}${envConfig.API_VERSION}/company-report-tool/project-note-delete`, {
        id,
      }, {
        headers: { Authorization: token },
      });
      fetchNotes(page * rowsPerPage, rowsPerPage, searchQuery);
    } catch (err) {
      console.error('Error deleting note:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h6">Notes</Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <TextField
            placeholder="Search notes..."
            value={searchQuery}
            onChange={e => handleSearch(e.target.value)}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: '#888' }} />
                </InputAdornment>
              ),
            }}
          />
        </div>
        <div style={{ marginTop: 32 }}>
          {notes.length > 0 && (
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 20, 50, 100]}
              labelRowsPerPage="Rows per page:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
              style={{ marginBottom: 8 }}
            />
          )}
          {loading && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 20 }}>
              <CircularProgress size={24} />
            </div>
          )}
          {!loading && notes.length === 0 && (
            <div style={{ color: '#888', textAlign: 'center', padding: 20 }}>No notes found.</div>
          )}
          {notes.map((note) => (
            <div key={note.company_report_tool_product_id} style={{ marginBottom: 12, borderBottom: '1px solid #eee', paddingBottom: 12 }}>
              {editingId === note.company_report_tool_product_id ? (
                <>
                  <TextField
                    style={{background:'#fbf8c3'}}
                    multiline
                    minRows={2}
                    value={editingText}
                    onChange={e => setEditingText(e.target.value)}
                    fullWidth
                  />
                  <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                    <Button size="small" color="primary" onClick={() => handleUpdateNote(note.company_report_tool_product_id)} disabled={loading}>
                      Save
                    </Button>
                    <Button size="small" onClick={() => setEditingId(null)}>
                      Cancel
                    </Button>
                  </div>
                </>
              ) : (
                <>                
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', background:'#f4f4f4', padding: '8px 12px', borderRadius: 4 }}>
                    <div style={{display:'flex', flexDirection: 'column', alignItems:'left'}}>
                      <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: 4 }}>
                      #{note.company_report_tool_product_id}-{note.company_report_tool_product_name}
                      </Typography>
                      <div style={{display:'flex', gap: 8, fontSize: 12, color: '#666'}}>
                        <span>{note.edited_at}</span>
                        {note.edited_by && <span>• {note.edited_by}</span>}
                      </div>
                    </div>
                    <div style={{display:'flex', gap:8, alignItems:'center'}}>
                      {note.editable > 0 && (
                        <>
                          <IconButton size="small" onClick={() => handleEditNote(note)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => handleDeleteNote(note.company_report_tool_product_id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </div>
                  </div>
                  <div style={{ fontSize: 14, margin: '8px 0 0 0', padding: '0 12px' }}>{note.note}</div>
                </>
              )}
            </div>
          ))}
          {notes.length > 0 && (
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 20, 50, 100]}
              labelRowsPerPage="Rows per page:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
              style={{ marginTop: 16 }}
            />
          )}
        </div>
      </DialogContent>
      {/* <DialogActions>
        <Button onClick={onClose} color="primary" disabled={loading}>
          Close
        </Button>
      </DialogActions> */}
    </Dialog>
  );
} 